// Utility function to convert File to base64
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

// Get MIME type from file
export const getFileType = (file: File): string => {
  return file.type || 'application/octet-stream';
};

// Validate file size (max 2MB by default)
export const validateFileSize = (file: File, maxSizeInMB: number = 2): boolean => {
  if (!file) return false;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size > 0 && file.size <= maxSizeInBytes;
};

// Dangerous executable and script file extensions blocklist
export const BLOCKED_FILE_EXTENSIONS = [
  'exe', 'dll', 'bat', 'cmd', 'sh', 'php', 'phtml', 'php3', 'php4', 'php5',
  'phps', 'cgi', 'pl', 'asp', 'aspx', 'jsp', 'jspx', 'js', 'vbs', 'scr',
  'jar', 'war', 'msi', 'com', 'hta', 'ps1', 'py', 'rb', 'html', 'htm', 'xhtml', 'svg'
];

export const ALLOWED_FILE_TYPES = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'txt'];
export const MAX_FILE_SIZE_MB = 2;

// Standard MIME type mapping for permitted document types
export const MIME_TYPE_MAP: Record<string, string[]> = {
  pdf: ['application/pdf'],
  doc: ['application/msword'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  jpg: ['image/jpeg', 'image/pjpeg'],
  jpeg: ['image/jpeg', 'image/pjpeg'],
  png: ['image/png'],
  txt: ['text/plain'],
};

// Validate file type by extension
export const validateFileType = (file: File, allowedTypes: string[] = ALLOWED_FILE_TYPES): boolean => {
  if (!file || !file.name) return false;
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

  // 1. Immediately reject if extension matches dangerous executable extensions
  if (BLOCKED_FILE_EXTENSIONS.includes(fileExtension)) {
    return false;
  }

  // 2. Reject double extensions with executable suffix (e.g. file.pdf.exe)
  const nameParts = file.name.split('.');
  if (nameParts.length > 2) {
    const secondToLast = nameParts[nameParts.length - 2].toLowerCase();
    if (BLOCKED_FILE_EXTENSIONS.includes(secondToLast)) {
      return false;
    }
  }

  return allowedTypes.includes(fileExtension);
};

// Sanitize filename to prevent path traversal, null bytes, and control characters
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return 'unnamed_file';
  // Strip directory paths, null bytes, control characters
  return fileName
    .replace(/[/\\]/g, '_')
    .replace(/\0/g, '')
    .replace(/[^\w\s.-]/gi, '')
    .trim() || 'unnamed_file';
};

export interface FileValidationResult {
  valid: boolean;
  error?: string;
  sanitizedName?: string;
}

/**
 * Comprehensive frontend file validator
 */
export const validateUploadFile = (
  file: File,
  allowedTypes: string[] = ALLOWED_FILE_TYPES,
  maxSizeInMB: number = MAX_FILE_SIZE_MB
): FileValidationResult => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  if (file.size === 0) {
    return { valid: false, error: 'File is empty (0 bytes)' };
  }

  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  if (BLOCKED_FILE_EXTENSIONS.includes(ext)) {
    return { valid: false, error: `Executable or script files (.${ext}) are not permitted.` };
  }

  if (!validateFileType(file, allowedTypes)) {
    return { valid: false, error: `Invalid file format. Allowed formats: ${allowedTypes.join(', ')}.` };
  }

  if (!validateFileSize(file, maxSizeInMB)) {
    return { valid: false, error: `File size exceeds the maximum limit of ${maxSizeInMB} MB.` };
  }

  return {
    valid: true,
    sanitizedName: sanitizeFileName(file.name),
  };
};

// Format file size for display
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

